/**
 * Compact relative-time formatting for the Tasks board work-time meta line.
 *
 * Migration-free: the board derives "active since / last worked" from the
 * `pa_task_context` timestamps (min/max created_at) with a `tasks.created_at`
 * fallback (validated 2026-07-14: ~43% of board tasks have no context yet, so
 * the fallback is required — an un-worked task must NOT show a fabricated
 * "last worked" from a status-flip `updated_at`).
 *
 * `elapsedShort` returns a bare magnitude token ("4m", "3h", "2d", "1w", "3mo")
 * for the span between an ISO timestamp and `nowMs`. Callers wrap it (see
 * {@link formatTaskTiming}). Sub-minute / future / unparseable spans read
 * "<1m" (null → ""), so the caller never renders a negative or NaN age.
 */
export function elapsedShort(fromIso: string | null, nowMs: number): string {
  if (fromIso === null) return "";
  const then = Date.parse(fromIso);
  if (Number.isNaN(then)) return "";
  const secs = Math.floor((nowMs - then) / 1000);
  if (secs < 60) return "<1m";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

/**
 * The board task's work-time meta string.
 *   • worked task (has both context bounds) →
 *       "active 2d · last worked 12m ago"   (real agent effort span)
 *   • un-worked task (no context) →
 *       "created 5d ago · not yet worked"   (honest: nothing has run on it)
 */
export function formatTaskTiming(
  args: {
    readonly work_started: string | null;
    readonly last_worked: string | null;
    readonly created_at: string;
  },
  nowMs: number,
): string {
  if (args.work_started !== null && args.last_worked !== null) {
    return `active ${elapsedShort(args.work_started, nowMs)} · last worked ${elapsedShort(
      args.last_worked,
      nowMs,
    )} ago`;
  }
  return `created ${elapsedShort(args.created_at, nowMs)} ago · not yet worked`;
}
